// File: packages/components/src/agent/AgentAvatarStage.tsx (Refactored & Fixed)

import React, { useMemo } from 'react';
import { Card, Badge } from '@dopomogai/ui';
// --- CORRECTED HOOK IMPORTS ---
import { useCalculator } from '@dopomogai/agent-kit';
import { useTotals } from '@dopomogai/agent-kit';
// -----------------------------
import { CosmeticBadgesOverlay } from './CosmeticBadgesOverlay';
import type { TierId } from '@dopomogai/types';

type Props = {
  // Accept "videos" but gracefully render images if these URLs are not video files.
  videos: { trainee?: string; junior?: string; middle?: string; senior?: string; poster?: string };
  // The prop on the child is required, so we make it required here too.
  badgeRenderer: (id: string) => React.ReactNode;
  ariaLabel?: string;
};

/**
 * Visually represents the agent's evolution based on the selected plan tier
 * and features. It now derives its state from the `useTotals` and `useCalculator` hooks.
 */
export function AgentAvatarStage({ videos, badgeRenderer, ariaLabel = 'Agent avatar' }: Props) {
  // --- New Hook-based State Derivation ---
  
  // 1. Get the base spec and state from the context provider.
  const { spec, state } = useCalculator();
  
  // 2. Pass them to useTotals to get derived values.
  const { totals } = useTotals(spec, state);

  // 3. Use the new `currentTier` property from our enhanced hook.
  const tier = totals.currentTier;

  // Derive the list of cosmetic badge IDs from the selected features.
  const cosmeticIds = useMemo(() => {
    const cosmetics = new Set<string>();
    // Safely access the new `cosmetics_map` property on the spec.
    if (spec.cosmetics_map) {
      for (const selectedId of state.selectedModuleIds) {
        const badges = spec.cosmetics_map[selectedId];
        // Ensure the callback parameter is typed.
        if (badges) {
          badges.forEach((badgeId: string) => cosmetics.add(badgeId));
        }
      }
    }
    return Array.from(cosmetics);
  }, [state.selectedModuleIds, spec.cosmetics_map]);
  // --- End of New Logic ---

  const tierSrc =
    tier === 'senior' ? videos.senior :
    tier === 'middle' ? videos.middle :
    tier === 'junior' ? videos.junior :
    videos.trainee;

  const looksLikeVideo = (url?: string) => {
    if (!url) return false;
    const u = url.toLowerCase();
    return u.endsWith('.webm') || u.endsWith('.mp4') || u.includes('video');
  };

  return (
    <Card noPadding className="relative overflow-hidden rounded-xl">
      <div className="absolute z-10 m-2">
        <Badge text={tier.toUpperCase() as string} accentColor="blue" />
      </div>

      <CosmeticBadgesOverlay badgeIds={cosmeticIds} renderBadge={badgeRenderer} />

      {looksLikeVideo(tierSrc) ? (
        <video
          key={`vid-${tier}`}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={videos.poster}
          className="w-full h-[340px] object-cover bg-slate-50"
          aria-label={ariaLabel}
        >
          {/* prefer webm, then mp4 if provided */}
          {tierSrc?.toLowerCase().endsWith('.webm') && <source src={tierSrc} type="video/webm" />}
          {tierSrc?.toLowerCase().endsWith('.mp4') && <source src={tierSrc} type="video/mp4" />}
        </video>
      ) : (
        <img
          key={`img-${tier}`}
          src={tierSrc || videos.poster}
          alt={ariaLabel}
          className="w-full h-[340px] object-cover bg-slate-50"
        />
      )}
    </Card>
  );
}